import Global from './lib/global';
import Logger from './lib/logger';
import { getNostoClient, recordNosto, flushSearchSubmission } from './lib/nosto-client.js';
import { FACETS, PAGE_SIZE, fallbackUrl, fragmentUrl, latestRequest, productHandle, readSearchState, searchRequest, updateSearchUrl, usesNosto, validateProducts, withTimeout } from './lib/search-state.js';

const root = document.querySelector('[data-nosto-search]');

async function initSearch() {
  if (!root) return;
  clearTimeout(window.themeSearchWatchdog);
  if (!usesNosto(window.location.href, window.themeTrackingSuppressed)) {
    document.documentElement.classList.remove('nosto-search-active');
    document.title = root.dataset.nativeTitle;
    const notice = document.querySelector('[data-search-fallback]');
    if (notice) notice.hidden = new URL(window.location.href).searchParams.get('search_provider') !== 'shopify';
    return;
  }

  root.hidden = false;
  const copy = JSON.parse(root.querySelector('[data-search-copy]').textContent);
  const find = selector => root.querySelector(selector);
  const nextRequest = latestRequest();
  let committedProducts;
  let committedKey;
  let client;

  function navigate(url) {
    root.querySelectorAll('details[open]').forEach(details => { details.open = false; });
    window.history.pushState(null, '', url);
    void render();
  }

  async function fetchFragment(url, signal, selector) {
    const response = await withTimeout(fetch(url, { signal, credentials: 'same-origin' }));
    if (!response.ok) throw new Error(`Search rendering failed: ${response.status}`);
    const html = await withTimeout(response.text());
    const fragment = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    if (!fragment) throw new Error('Search fragment is missing');
    return fragment;
  }

  async function loadCards(hits, signal) {
    const entries = hits.map(hit => ({ hit, handle: productHandle(hit, window.location.href) }));
    const batches = [];
    for (let i = 0; i < entries.length; i += 20) {
      const handles = entries.slice(i, i + 20).map(entry => entry.handle);
      batches.push(fetchFragment(fragmentUrl(window.location.href, root.dataset.searchUrl, 'nosto-cards', handles.join('|')), signal, '[data-search-card-batch]'));
    }
    const fragments = await Promise.all(batches);
    const cards = fragments.flatMap(fragment => Array.from(fragment.querySelectorAll('[data-search-product-id]')));
    return entries.map(({ hit, handle }) => {
      const card = cards.find(element => element.dataset.searchProductId === String(hit.productId) && element.dataset.searchHandle === handle);
      if (!card) throw new Error(`Product card is unavailable: ${hit.productId}`);
      card.querySelectorAll('a[href]').forEach(link => {
        link.addEventListener('click', () => recordNosto(client, 'recordSearchClick', 'serp', hit), { capture: true });
      });
      return card;
    });
  }

  function renderPagination(container, current, pages, content = false) {
    container.replaceChildren();
    container.hidden = pages <= 1;
    if (pages <= 1) return;
    const list = document.createElement('div');
    list.className = 'pagination';
    const add = (page, label, active = false) => {
      const link = document.createElement('a');
      link.href = updateSearchUrl(window.location.href, content ? { contentPage: page } : { page });
      link.dataset.searchNavigation = 'true';
      link.textContent = label;
      link.setAttribute('aria-label', label === String(page) ? copy.page.replace('[page]', page) : label);
      if (active) link.setAttribute('aria-current', 'page');
      list.append(link);
    };
    if (current > 1) add(current - 1, copy.previous);
    const numbers = [...new Set([1, current - 1, current, current + 1, pages])].filter(page => page >= 1 && page <= pages).sort((a, b) => a - b);
    numbers.forEach((page, index) => {
      if (index && page - numbers[index - 1] > 1) {
        const gap = document.createElement('span');
        gap.textContent = '…';
        list.append(gap);
      }
      add(page, String(page), page === current);
    });
    if (current < pages) add(current + 1, copy.next);
    container.append(list);
  }

  function renderFacets(products, state) {
    FACETS.forEach(group => {
      const facet = products.facets?.find(item => item.field === `extra.${group}` && item.type === 'terms');
      const values = new Map((facet?.data || []).map(item => [item.value, item]));
      state.filters[group].forEach(value => { if (!values.has(value)) values.set(value, { value, count: 0 }); });
      const container = find(`[data-facet-options="${group}"]`);
      container.replaceChildren();
      find(`[data-facet-group="${group}"]`).hidden = values.size === 0;
      [...values.values()].sort((a, b) => a.value.localeCompare(b.value)).forEach(item => {
        const selected = state.filters[group].includes(item.value);
        const li = document.createElement('li');
        const label = document.createElement('label');
        label.className = `selected-search-filter-type-list${selected ? ' clicked-type-custom' : ''}`;
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.name = `filter.${group}`;
        input.value = item.value;
        input.checked = selected;
        const checkmark = document.createElement('span');
        checkmark.className = 'checkmark';
        checkmark.setAttribute('aria-hidden', 'true');
        label.append(input, checkmark, document.createTextNode(`${item.value.replace(/-/g, ' ')} (${item.count})`));
        li.append(label);
        container.append(li);
      });
    });
    find('[data-clear-filters]').hidden = !FACETS.some(group => state.filters[group].length);
    root.querySelectorAll('[data-sort]').forEach(button => {
      button.setAttribute('aria-pressed', String(button.dataset.sort === state.sort));
      button.parentElement.classList.toggle('selectedHighlight', button.dataset.sort === state.sort);
    });
  }

  async function render() {
    const requestState = nextRequest();
    const state = readSearchState(window.location.href);
    if (!state.query.trim()) { window.location.reload(); return; }
    const request = searchRequest(state);
    const key = JSON.stringify(request);
    const heading = find('[data-search-heading]');
    const term = document.createElement('span');
    term.className = 'term';
    term.textContent = state.query;
    heading.replaceChildren(document.createTextNode(`${copy.heading} `), term);
    root.setAttribute('aria-busy', 'true');
    find('[data-search-status]').hidden = false;
    find('[data-search-status]').textContent = copy.loading;
    find('[data-search-controls]').hidden = true;
    find('[data-product-results]').hidden = true;
    find('[data-content-results]').hidden = true;
    const empty = document.querySelector('[data-search-empty]');
    if (empty) empty.hidden = true;

    const contentPromise = fetchFragment(fragmentUrl(window.location.href, root.dataset.searchUrl, 'nosto-content', state.query, state.contentPage), requestState.signal, '[data-search-content-batch]').catch(error => ({ error }));
    try {
      client = await getNostoClient();
      if (!requestState.isCurrent()) return;
      let storage;
      try { storage = sessionStorage; } catch (error) {}
      flushSearchSubmission(client, state.query, storage);
      let data = key === committedKey ? committedProducts : undefined;
      if (!data) {
        const response = await withTimeout(client.search(request, { redirect: false }));
        if (!requestState.isCurrent()) return;
        if (response?.redirect) {
          const target = new URL(response.redirect, window.location.href);
          if (!['http:', 'https:'].includes(target.protocol)) throw new Error('Invalid search redirect');
          window.location.assign(target.href);
          return;
        }
        const products = validateProducts(response);
        const pages = Math.ceil(products.total / PAGE_SIZE);
        if (pages && state.page > pages) {
          window.history.replaceState(null, '', updateSearchUrl(window.location.href, { page: pages }));
          void render();
          return;
        }
        const cards = await loadCards(products.hits, requestState.signal);
        data = { products, response, cards };
      }
      const content = await contentPromise;
      if (!requestState.isCurrent()) return;
      const count = (data.products.total === 1 ? copy.result : copy.results).replace('[count]', data.products.total);
      find('[data-product-count]').textContent = count;
      document.title = `${count} – ${state.query} – ${root.dataset.shopName}`;
      renderFacets(data.products, state);
      find('[data-product-grid]').replaceChildren(...data.cards);
      find('[data-product-results]').hidden = data.products.total === 0;
      find('[data-search-status]').textContent = data.products.total === 0 ? copy.empty : count;
      find('[data-search-status]').hidden = data.products.total > 0;
      find('[data-search-controls]').hidden = false;
      renderPagination(find('[data-product-pagination]'), state.page, Math.ceil(data.products.total / PAGE_SIZE));

      const contentBody = find('[data-content-body]');
      contentBody.replaceChildren();
      if (content.error) {
        find('[data-content-results]').hidden = false;
        find('[data-content-count]').textContent = '';
        find('[data-content-pagination]').hidden = true;
        const message = document.createElement('p');
        message.textContent = copy.contentError;
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.textContent = copy.retry;
        retry.addEventListener('click', () => navigate(updateSearchUrl(window.location.href, { contentPage: 1 })));
        contentBody.append(message, retry);
      } else {
        const contentTotal = Number(content.dataset.total);
        find('[data-content-results]').hidden = contentTotal === 0;
        find('[data-content-count]').textContent = `(${contentTotal})`;
        contentBody.append(content);
        renderPagination(find('[data-content-pagination]'), Number(content.dataset.page), Number(content.dataset.pages), true);
        if (empty && data.products.total === 0 && contentTotal === 0) {
          empty.dataset.nostoEmpty = 'true';
          empty.hidden = false;
        }
      }
      if (key !== committedKey) {
        let isKeyword = false;
        try {
          isKeyword = sessionStorage.getItem('nosto-search-keyword') === state.query;
          sessionStorage.removeItem('nosto-search-keyword');
        } catch (error) { isKeyword = false; }
        recordNosto(client, 'recordSearch', 'serp', request, data.response, { isKeyword });
      }
      committedKey = key;
      committedProducts = data;
      root.setAttribute('aria-busy', 'false');
      try { window.Zonos?.displayCurrency?.(); } catch (error) { Logger.error('Search currency conversion', error); }
    } catch (error) {
      if (!requestState.isCurrent()) return;
      Logger.error('Nosto search failed', error);
      window.location.replace(fallbackUrl(window.location.href).href);
    }
  }

  root.addEventListener('change', event => {
    if (event.target.matches('input[type="checkbox"]')) event.target.closest('label').classList.toggle('clicked-type-custom', event.target.checked);
  });
  root.addEventListener('click', event => {
    const navigation = event.target.closest('[data-search-navigation]');
    if (navigation && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && event.button === 0) {
      event.preventDefault();
      navigate(navigation.href);
    }
    if (event.target.closest('[data-toggle-filters]')) {
      const open = find('[data-filter-groups]').classList.toggle('showFiltering');
      find('[data-toggle-filters]').setAttribute('aria-expanded', String(open));
    }
    if (event.target.closest('[data-apply-filters]')) {
      const filters = Object.fromEntries(FACETS.map(group => [group, Array.from(root.querySelectorAll(`input[name="filter.${group}"]:checked`), input => input.value)]));
      navigate(updateSearchUrl(window.location.href, { filters }));
    }
    if (event.target.closest('[data-clear-filters]')) navigate(updateSearchUrl(window.location.href, { filters: {} }));
    const sort = event.target.closest('[data-sort]');
    if (sort) navigate(updateSearchUrl(window.location.href, { sort: sort.dataset.sort }));
  });
  window.addEventListener('popstate', () => void render());
  await render();
}

Global();
void initSearch().catch(error => {
  Logger.error('Search initialization failed', error);
  if (usesNosto(window.location.href, window.themeTrackingSuppressed)) window.location.replace(fallbackUrl(window.location.href).href);
});
